import type { User } from '@/account'

import { defineStore } from 'pinia'
import { useStorage } from '@vueuse/core'
import axios from '@/axios'
import { isAxiosError } from 'axios'
import { isNative } from '@/helpers'
import { store } from '@/storehandler/tokens'
import { i18n, type SupportedLocale } from '@/i18n'

const instance = axios.create({
  baseURL: import.meta.env.VITE_GATEWAY_URL + '/user'
})

function applyLocale(language: string | undefined) {
  const supported = ['en-US', 'sv-SE']
  if (language && supported.includes(language)) {
    i18n.global.locale.value = language as SupportedLocale
  }
}

const jsonSerializer = {
  read: (v: any) => (v ? JSON.parse(v) : null),
  write: (v: any) => JSON.stringify(v)
}

export enum LoginResponse {
  Success = 'success',
  TwoFactorRequired = '2fa-required',
  PhoneRequired = 'phone-required',
  SiteOwnerNoMobile = 'site-owner-no-mobile',
  CompanyAdminNoMobile = 'company-admin-no-mobile',
  Unknown = 'unknown'
}

export enum DeleteAccountResponse {
  Success = 'success',
  RoleNotAllowed = 'role-not-allowed',
  Unknown = 'unknown'
}

export const useAccountStore = defineStore({
  id: 'account',
  state: () => ({
    user: useStorage<User | null>('account.user', null, undefined, {
      serializer: jsonSerializer
    }),
    two_factor_required: false,
    phone_required: false,
    login_form: {
      email: '',
      password: '',
      remember: false
    },
    login_pending_phone: '',
    register_form: null as null | {
      email: string
      firstname: string
      lastname: string
      phone: string
      password: string
      subscribe: boolean
      terms: boolean
      company?: string
      companyNo?: string
      billingAddress?: string
      billingZip?: string
      billingCity?: string
      bankTransferNo?: string
      contactPerson?: string
    },
    register_flow: null as null | 'user' | 'siteowner',
    firebase_id: '',
    favorites: [] as string[]
  }),
  getters: {
    isLoggedIn: (state) => !!state.user,
    hasRole:
      (state) =>
      (role: string): boolean => {
        return !!state.user?.roles?.find((r) => r.type === role)
      }
  },
  actions: {
    async login({
      email,
      password,
      remember,
      trusted,
      code,
      phone
    }: {
      email?: string
      password?: string
      remember?: boolean
      trusted?: boolean
      code?: string
      phone?: string
    }): Promise<string> {
      if (email && password) {
        this.login_form.email = email
        this.login_form.password = password
        this.login_form.remember = !!remember
      }

      try {
        const response = await instance.post('/login', {
          email: email || this.login_form.email,
          password: password || this.login_form.password,
          remember: remember ?? this.login_form.remember,
          trusted,
          code,
          phone: phone || this.login_pending_phone || undefined,
          language: i18n.global.locale.value
        })
        this.two_factor_required = response.data.two_factor === true
        this.phone_required = response.data.phone_required === true

        if (!this.two_factor_required && !this.phone_required) {
          this.login_form.email = ''
          this.login_form.password = ''
          this.login_form.remember = false
          this.login_pending_phone = ''

          const roles =
            response.data.user.roles?.map(
              (role: { type: string }) => role.type
            ) || []
          if (isNative) {
            if (roles.includes('siteowner')) {
              return LoginResponse.SiteOwnerNoMobile
            }
            if (roles.includes('companyadmin')) {
              return LoginResponse.CompanyAdminNoMobile
            }
          }

          this.user = response.data.user
          applyLocale(this.user?.language)
        }
      } catch (error: unknown) {
        console.error(error)
        if (error instanceof Error && error.message) {
          return error.message
        }
        return LoginResponse.Unknown
      }

      if (this.phone_required) {
        return LoginResponse.PhoneRequired
      }
      return this.two_factor_required
        ? LoginResponse.TwoFactorRequired
        : LoginResponse.Success
    },
    async verify(): Promise<boolean> {
      try {
        const resp = await instance.get('/verify')
        const ok = resp.status === 200

        if (ok && !this.user) {
          this.refreshUser() // Parent should catch any thrown errors
        } else if (ok && this.user) {
          applyLocale(this.user.language)
        }

        return ok
      } catch (error) {
        this.logout()
        console.error(error)
        return false
      }
    },
    async logout(): Promise<void> {
      this.user = null
      this.login_pending_phone = ''
      store.remove('tt-token')
    },
    async deleteAccount(): Promise<DeleteAccountResponse> {
      try {
        await instance.delete('/delete-account')
        return DeleteAccountResponse.Success
      } catch (error) {
        console.error(error)
        if (isAxiosError(error)) {
          const backendError = String(error.response?.data?.error || '').toLowerCase()
          if (backendError.includes('account deletion is not allowed')) {
            return DeleteAccountResponse.RoleNotAllowed
          }
        }
        return DeleteAccountResponse.Unknown
      }
    },
    setRegisterForm(
      form: NonNullable<typeof this.register_form>,
      flow: 'user' | 'siteowner'
    ): void {
      this.register_form = { ...form }
      this.register_flow = flow
    },
    async sendRegistrationCode(): Promise<string | true> {
      if (!this.register_form?.email) {
        return 'error.fieldRequired'
      }
      if (!this.register_form?.phone) {
        return 'error.invalid_phone_number'
      }
      try {
        await instance.post('/register/send-code', {
          email: this.register_form.email,
          phone: this.register_form.phone,
          language: i18n.global.locale.value
        })
        return true
      } catch (error: unknown) {
        console.error(error)
        if (error instanceof Error) {
          return error.message
        }
        return 'error.unknown'
      }
    },
    async completeRegistration(code: string): Promise<string | true> {
      if (!this.register_form) {
        return 'error.unknown'
      }
      try {
        const response = await instance.post('/register', {
          ...this.register_form,
          code,
          language: i18n.global.locale.value
        })
        this.user = response.data.user
        applyLocale(this.user?.language)
        this.register_form = null
        this.register_flow = null
        return true
      } catch (error: unknown) {
        console.error(error)
        if (error instanceof Error) {
          return error.message
        }
        return 'error.unknown'
      }
    },
    async getOnboardingLink(): Promise<string> {
      return (
        (
          await instance.post('/onboarding', {
            return: window.location.origin,
            refresh: window.location.origin,
            country_code: 'SE'
          })
        ).data?.url || ''
      )
    },
    async getStripeDashboardLink(): Promise<string> {
      return (await instance.get('/stripe-login')).data?.url || ''
    },
    async forgotPassword(email: string): Promise<boolean> {
      return (
        await instance.post('/reset-password', {
          email,
          language: i18n.global.locale.value
        })
      )?.status === 200
    },
    async resetPassword(
      email: string,
      password: string,
      token: string
    ): Promise<boolean> {
      return (
        (await instance.post('/reset-password', { email, password, token }))
          ?.status === 200
      )
    },
    async refreshUser(): Promise<boolean> {
      try {
        const response = await instance.get('/me')
        this.user = response.data
        applyLocale(this.user?.language)
        return true
      } catch (error: unknown) {
        console.error(error)
        return false
      }
    },

    async updateLanguage(language: string): Promise<boolean> {
      if (!this.user) return false
      try {
        await instance.post(`/${this.user.id}/update`, {
          email: this.user.email,
          phone: this.user.phone,
          language
        })
        this.user.language = language
        applyLocale(language)
        return true
      } catch (error: unknown) {
        console.error(error)
        return false
      }
    },

    // 2FA
    async resend2FA(email: string, phone?: string): Promise<boolean> {
      try {
        await instance.post('/2fa/resend', {
          email,
          phone: phone || this.login_pending_phone || undefined,
          language: i18n.global.locale.value
        })
        return true
      } catch (error: unknown) {
        console.error(error)
        return false
      }
    },
    setFirebaseID(id: string): void {
      this.firebase_id = id
    },
    setUser(user: User): void {
      this.user = user
    }
  }
})
